import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import { useSelectedOrganizationName } from '@client/store/organization';
import { useProjectTemplates } from '@client/store/project';
import AddIcon from '@mui/icons-material/Add';
import { isTemplateOrganizationItem } from '@shared/helpers/organization.helpers';
import { IProjectInfo } from '@shared/models/project.model';
import React, { useCallback, useMemo, useState } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_PROJECT_NEW_PROJECT_BUTTON_CLICKED } from '@shared/constants';
import { useEditProjectDialogStore } from '../EditProject/edit-project.store';
import { PublicTemplatesMenu } from '../CreateProjectMenu/components/PublicTemplatesMenu';
import { TemplateProjectItem } from '../CreateProjectMenu/components/TemplateProjectItem';
import { useUIState } from '@client/store/ui';
import { Menu } from '@ui/menus/Menu';
import { useOpenPaywallDialog } from '../Paywall/paywall.hook';
import { Button, ButtonProps } from '@ui/buttons';
import { GhostButton } from '@ui/GhostButton';

type CreateProjectMenuProps = {
  buttonSize?: ButtonProps['size'];
  buttonColor?: ButtonProps['color'];
  buttonLabel?: string;
  headerStyle?: boolean;
};

export const CreateProjectMenu: React.FC<CreateProjectMenuProps> = ({
  buttonSize = 'small',
  buttonColor = 'secondary',
  buttonLabel = 'New project',
  headerStyle = false,
}) => {
  const templates = useProjectTemplates();
  const selectedOrganization = useSelectedOrganizationName();
  const { selectedProjectFolderId: folderId } = useUIState(
    'selectedProjectFolderId',
  );
  const { openPaywallDialog, isBehindPaywall: shouldOpenPaywallDialog } =
    useOpenPaywallDialog();
  const { set: openEditProjectDialog } = useEditProjectDialogStore();

  const sortedTemplates = useMemo(
    () => getSortedTemplates(templates),
    [templates],
  );

  const [anchor, setAnchor] = useState<Element>();

  const createBlank = useCallback(() => {
    if (shouldOpenPaywallDialog('create-project')) {
      openPaywallDialog('create-project');
      return;
    }
    openEditProjectDialog({ type: 'project', mode: 'create', folderId });
    setAnchor(undefined);
  }, [
    folderId,
    openEditProjectDialog,
    shouldOpenPaywallDialog,
    openPaywallDialog,
  ]);

  const closeMenu = useCallback(() => {
    setAnchor(undefined);
  }, [setAnchor]);

  const openMenu = useMouseEventCallback((event) => {
    setAnchor(event.currentTarget);
    amplitudeLog(AMP_PROJECT_NEW_PROJECT_BUTTON_CLICKED);
  });

  const buttonProps = useObjectMemo<Omit<ButtonProps, 'children'>>({
    variant: 'outlined',
    size: buttonSize,
    color: buttonColor,
    startIcon: <AddIcon />,
  });

  return (
    <>
      {headerStyle ? (
        <GhostButton
          size={buttonSize === 'large' ? 'large' : 'default'}
          onClick={openMenu}
        >
          {buttonLabel}
        </GhostButton>
      ) : (
        <Button {...buttonProps} onClick={openMenu}>
          {buttonLabel}
        </Button>
      )}

      <Menu open={!!anchor} anchorEl={anchor} onClose={closeMenu} width={250}>
        {sortedTemplates.length > 0 && (
          <>
            <Menu.Group label="Create from template">
              {sortedTemplates.map((item) => (
                <TemplateProjectItem
                  key={item.id}
                  item={item}
                  folderId={folderId}
                  onClick={closeMenu}
                />
              ))}
            </Menu.Group>
            <Menu.Divider />
          </>
        )}

        {!isTemplateOrganizationItem(selectedOrganization) && (
          <PublicTemplatesMenu />
        )}

        <Menu.Item onClick={createBlank}>Create blank</Menu.Item>
      </Menu>
    </>
  );
};

export const getSortedTemplates = (templates: IProjectInfo[]) => {
  return templates
    .filter((template) => template.name)
    .sort((a, b) => a.name.localeCompare(b.name));
};
